import React, { useEffect, useState } from "react";
import { bp } from "../slug";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, FolderOpen, Compass, Check, X } from "lucide-react";
import { setMode } from "./Playground";

const KEY = "larus:welcome:done";
const STEPS = [
  { Icon: FlaskConical, title: "Start in the Playground", text: "Three fictional projects on real agreement templates. Nothing is sent, paid or signed: read them like a CFO would." },
  { Icon: FolderOpen, title: "Open a build", text: "A build is where the work is: one sentence on where it stands, four marks, your to-dos and the documents behind them." },
  { Icon: Compass, title: "Take the tour", text: "Six short stops explain the page. Bulbs stay on afterwards for anything you want explained again." },
];

/* Shown once, on first login, before anyone can see an empty Workspace. */
export function Welcome({ empty }: { empty: boolean }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const nav = useNavigate();
  useEffect(() => { if (!localStorage.getItem(KEY) && empty) setOpen(true); }, [empty]);
  if (!open) return null;
  const done = () => { localStorage.setItem(KEY, "1"); setOpen(false); };
  const go = () => { done(); setMode("playground"); localStorage.removeItem("larus:tour:folders"); nav(`${bp("p-syd01")}/build`); };
  const s = STEPS[i];
  return createPortal(<div className="welcome-mask" role="dialog" aria-modal="true" aria-label="Welcome" data-testid="welcome">
    <motion.div className="welcome" initial={{ opacity: 0, y: 20, scale: .98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: .4, ease: [0.2, 0.8, 0.2, 1] }}>
      <button className="welcome-x" onClick={done} aria-label="Skip" data-testid="welcome-skip"><X size={16} /></button>
      <small className="x-kicker">Welcome to LARUS</small>
      <h1>Three steps and you will never see an empty screen.</h1>
      <ol className="welcome-steps">{STEPS.map((st, k) => <li key={k} className={k === i ? "cur" : k < i ? "done" : ""} onClick={() => setI(k)} data-testid={`welcome-step-${k + 1}`}><span className="welcome-ico">{k < i ? <Check size={16} /> : <st.Icon size={18} />}</span><span><b>{st.title}</b></span></li>)}</ol>
      <AnimatePresence mode="wait"><motion.p key={i} className="welcome-text" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: .2 }} data-testid="welcome-text"><s.Icon size={16} /> {s.text}</motion.p></AnimatePresence>
      <div className="welcome-actions">
        <button className="ghost" onClick={done} data-testid="welcome-later">I know my way around</button>
        {i < STEPS.length - 1 ? <button className="primary" onClick={() => setI(i + 1)} data-testid="welcome-next">Next →</button> : <button className="primary" onClick={go} data-testid="welcome-go">Open the Playground and start the tour</button>}
      </div>
    </motion.div>
  </div>, document.body);
}
